/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { getEventId } from '@e180/domain/lib/event/util';

import { loginWithDoubleDutch } from 'src/shared/app/authentication/action/base/authenticationActions';
import withFeatureDecisions from 'src/shared/app/feature/component/withFeatureDecisions';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { homeRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { getDisplayName } from 'src/shared/core/component/hoc/util/hocUtil';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';

import type { BraindateEvent } from '@e180/domain/lib/event/type';
import type { State } from 'src/shared/core/type/reduxTypes';

type Props = {
  braindateEvent: BraindateEvent,
  // Self passed
  history: Object,
  dispatch: any,
  featureDecisions: Object,
};

export default function withDoubleDutch(WrappedComponent: any): any {
  const WithDoubleDutchComponent = (props: Props): React.Node => {
    const { braindateEvent, dispatch, history, featureDecisions } = props;

    /*
     |--------------------------------------------------------------------------
     | Hooks
     |--------------------------------------------------------------------------
     */

    const [homePath] = useRoutes(homeRoute);

    /*
     |--------------------------------------------------------------------------
     | Effects
     |--------------------------------------------------------------------------
     */

    React.useEffect(() => {
      if (featureDecisions.isDoubleDutchIntegrationEnabled()) {
        if (window.DD) {
          initDoubleDutch();
        } else if (window.loadJS) {
          const script = window.loadJS(
            'https://cdn.rawgit.com/doubledutch/sdk/master/DDEventsSDK.js',
            initDoubleDutch,
          );

          script.onerror = () => {
            // TODO: handle error
          };
        }
      }
    }, []);

    /*
     |----------------------------------------------------------------------------
     | Functions
     |----------------------------------------------------------------------------
     */

    const initDoubleDutch = () => {
      const { DD } = window;

      DD.Events.onReady(() => {
        DD.Events.getSignedAPIAsync('users/me', '', (signedUrl, authHeader) => {
          dispatch(
            loginWithDoubleDutch(
              getEventId(braindateEvent),
              authHeader,
              signedUrl,
            ),
          )
            .then(() => history.push(homePath))
            .catch(() => {
              // TODO
            });
        });
      });
    };

    /*
     |----------------------------------------------------------------------------
     | Elements
     |----------------------------------------------------------------------------
     */

    return <WrappedComponent {...props} />;
  };

  WithDoubleDutchComponent.displayName = `WithDoubleDutchComponent(${getDisplayName(
    WrappedComponent,
  )})`;

  return compose(
    withRouter,
    withFeatureDecisions,
    connect((state: State) => ({ braindateEvent: getEventSafely(state) })),
  )(WithDoubleDutchComponent);
}
